import { Instance, SnapshotIn, SnapshotOut, types } from "mobx-state-tree";
import {
  createSurveyRuleConditionModel,
  SurveyRuleConditionModel,
} from "./SurveyRuleConditionModel";
import { SurveyRuleAction } from "@pulse/pulse-rpcs/lib/survey-rules/surveyRuleAction";
import {
  createSurveyRuleTargetModel,
  SurveyRuleTargetModel,
} from "./SurveyRuleTargetModel";
import { LeoUUID } from "@surya-digital/leo-ts-runtime";
import { SurveyRuleValidationErrorGroupEnums } from "@pulse/pulse-rpcs/lib/survey-rules/surveyRuleValidationErrorGroup";
import { SurveyRuleActionValidationError } from "@pulse/pulse-rpcs/lib/survey-rules/surveyRuleActionValidationError";
import {
  getSurveyRuleValidationErrorValues,
  SurveyRuleCommonValidationError,
} from "../../utils/getSurveyRuleValidationErrorValues";
import { SurveyRuleConditionEnums } from "@pulse/pulse-rpcs/lib/survey-rules/surveyRuleCondition";

type ActionValidationErrors = Extract<
  SurveyRuleActionValidationError.SurveyRuleActionValidationError,
  | SurveyRuleActionValidationError.SurveyRuleActionValidationError.INVALID_ACTION_RESPONDENT
  | SurveyRuleActionValidationError.SurveyRuleActionValidationError.INVALID_ACTION_MULTI_CHOICE
>;

const actionValidationErrorValues = Object.values(
  SurveyRuleActionValidationError.SurveyRuleActionValidationError,
).filter((validationError): validationError is ActionValidationErrors =>
  [
    SurveyRuleActionValidationError.SurveyRuleActionValidationError
      .INVALID_ACTION_RESPONDENT,
    SurveyRuleActionValidationError.SurveyRuleActionValidationError
      .INVALID_ACTION_MULTI_CHOICE,
  ].includes(validationError),
);

type TargetValidationErrors = Extract<
  SurveyRuleActionValidationError.SurveyRuleActionValidationError,
  | SurveyRuleActionValidationError.SurveyRuleActionValidationError.INVALID_SKIP_TO_TARGET_COUNT
  | SurveyRuleActionValidationError.SurveyRuleActionValidationError.INVALID_TARGET
  | SurveyRuleActionValidationError.SurveyRuleActionValidationError.INVALID_ACTION_GROUP_CHILD
  | SurveyRuleActionValidationError.SurveyRuleActionValidationError.INVALID_TARGET_GRID_ROW
  | SurveyRuleActionValidationError.SurveyRuleActionValidationError.INVALID_ACTION_GRID_ROW
  | SurveyRuleActionValidationError.SurveyRuleActionValidationError.INVALID_KEY_TARGET_ORDER
>;

const targetValidationErrorValues = Object.values(
  SurveyRuleActionValidationError.SurveyRuleActionValidationError,
).filter((validationError): validationError is TargetValidationErrors =>
  [
    SurveyRuleActionValidationError.SurveyRuleActionValidationError
      .INVALID_SKIP_TO_TARGET_COUNT,
    SurveyRuleActionValidationError.SurveyRuleActionValidationError
      .INVALID_TARGET,
    SurveyRuleActionValidationError.SurveyRuleActionValidationError
      .INVALID_ACTION_GROUP_CHILD,
    SurveyRuleActionValidationError.SurveyRuleActionValidationError
      .INVALID_TARGET_GRID_ROW,
    SurveyRuleActionValidationError.SurveyRuleActionValidationError
      .INVALID_ACTION_GRID_ROW,
    SurveyRuleActionValidationError.SurveyRuleActionValidationError
      .INVALID_KEY_TARGET_ORDER,
  ].includes(validationError),
);

export const SurveyRuleModel = types
  .model("SurveyRuleModel", {
    ruleId: types.identifier,
    conditions: types.map(SurveyRuleConditionModel),
    conditionOrder: types.array(types.string),
    action: types.maybeNull(
      types.enumeration(
        "action",
        Object.values(SurveyRuleAction.SurveyRuleAction),
      ),
    ),
    actionValidationErrors: types.array(
      types.enumeration(
        "actionValidationErrors",
        getSurveyRuleValidationErrorValues(actionValidationErrorValues),
      ),
    ),
    target: types.array(SurveyRuleTargetModel),
    targetValidationErrors: types.array(
      types.enumeration(
        "targetValidationErrors",
        getSurveyRuleValidationErrorValues(targetValidationErrorValues),
      ),
    ),
  })
  .actions((store) => ({
    getNonNullCondition: (
      conditionId: string,
    ): Instance<typeof SurveyRuleConditionModel> => {
      const condition = store.conditions.get(conditionId);
      if (!condition) {
        throw new Error(
          `Survey rule condition with ID ${conditionId} not found`,
        );
      }
      return condition;
    },
  }))
  .actions((store) => ({
    setAction: (action: SurveyRuleAction.SurveyRuleAction): void => {
      store.actionValidationErrors.clear();
      store.action = action;
    },
    clearAction: (): void => {
      store.actionValidationErrors.clear();
      store.action = null;
    },
    setTarget: (targets: SnapshotOut<typeof SurveyRuleTargetModel>[]): void => {
      store.targetValidationErrors.clear();
      if (!targets.length) {
        store.targetValidationErrors.push(
          SurveyRuleCommonValidationError.EMPTY_VALUE,
        );
      }
      store.target.replace(targets.map(createSurveyRuleTargetModel));
    },
    clearValidationErrors: (): void => {
      store.actionValidationErrors.clear();
      store.targetValidationErrors.clear();
      store.conditions.forEach((condition) => {
        condition.clearValidationErrors();
      });
    },
    addNewCondition: (): void => {
      const lastConditionId = store.conditionOrder.at(-1);
      if (lastConditionId) {
        const lastCondition = store.conditions.get(lastConditionId);
        if (!lastCondition) {
          throw new Error(
            `Survey rule condition with ID ${lastConditionId} not found.`,
          );
        }
        lastCondition.setConnector(
          SurveyRuleConditionEnums.Connector.Connector.AND,
        );
      }
      const newCondition = createSurveyRuleConditionModel();
      store.conditions.put(newCondition);
      store.conditionOrder.push(newCondition.conditionId);
    },
    removeCondition(conditionId: string): void {
      store.conditionOrder.remove(conditionId);

      const condition = store.getNonNullCondition(conditionId);
      store.conditions.delete(conditionId);

      const lastConditionId = store.conditionOrder.at(-1);
      if (
        lastConditionId &&
        condition.connecter ===
          SurveyRuleConditionEnums.Connector.Connector.NONE
      ) {
        const lastCondition = store.getNonNullCondition(lastConditionId);
        lastCondition.connecter =
          SurveyRuleConditionEnums.Connector.Connector.NONE;
      }
    },
    setValidationErrors: (
      validationErrorTarget: SurveyRuleValidationErrorGroupEnums.Target.Target,
    ): void => {
      if (
        validationErrorTarget instanceof
        SurveyRuleValidationErrorGroupEnums.Target.Condition
      ) {
        const condition = store.getNonNullCondition(
          validationErrorTarget.conditionId.uuid,
        );
        condition.setValidationErrors(validationErrorTarget.validationErrors);
        return;
      }

      validationErrorTarget.validationErrors.forEach((validationError) => {
        switch (validationError) {
          case SurveyRuleActionValidationError.SurveyRuleActionValidationError
            .INVALID_ACTION_MULTI_CHOICE:
          case SurveyRuleActionValidationError.SurveyRuleActionValidationError
            .INVALID_ACTION_RESPONDENT:
            store.actionValidationErrors.push(validationError);
            break;
          case SurveyRuleActionValidationError.SurveyRuleActionValidationError
            .INVALID_KEY_TARGET_ORDER:
          case SurveyRuleActionValidationError.SurveyRuleActionValidationError
            .INVALID_ACTION_GROUP_CHILD:
          case SurveyRuleActionValidationError.SurveyRuleActionValidationError
            .INVALID_ACTION_GRID_ROW:
          case SurveyRuleActionValidationError.SurveyRuleActionValidationError
            .INVALID_SKIP_TO_TARGET_COUNT:
          case SurveyRuleActionValidationError.SurveyRuleActionValidationError
            .INVALID_TARGET:
          case SurveyRuleActionValidationError.SurveyRuleActionValidationError
            .INVALID_TARGET_GRID_ROW:
            store.targetValidationErrors.push(validationError);
            break;
          default:
            validationError satisfies never;
        }
      });
    },
  }));

export const createSurveyRuleModel = (
  surveyRule?: SnapshotIn<typeof SurveyRuleModel>,
): Instance<typeof SurveyRuleModel> =>
  SurveyRuleModel.create(
    surveyRule ?? {
      ruleId: new LeoUUID().uuid,
      conditions: {},
      conditionOrder: [],
      action: null,
      actionValidationErrors: [],
      target: [],
      targetValidationErrors: [],
    },
  );
